import { Component, Input, OnInit } from '@angular/core';
import { AlarmOverview, Application } from '../../../interfaces/alarm';
import { getPumpOptions, getPumpType, systemsToPumpDevice, systemsToValveDevice } from '../../../utils/data-point-utils';
import { DataPointsResult, SystemDevice } from '../../../interfaces/data-points';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SchematicsService } from '../../../services/schematics.service';
import { AppModel, EEquipment, HotWaterTankSystemState } from '@ams/dumbledore';
import { PumpOption } from 'projects/customerportal/src/app/services/installation-configuration.service';
import { Installation } from '../../../interfaces/facilty';
import { Serie, SerieType, TemperatureChartTileComponent } from '../../charts/temperature-chart-tile/temperature-chart-tile.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { systemHasEnergyValve } from 'projects/dumbledore/src/schematic/util/system-properties-util';
import { PasteurizationStatusTileComponent } from '../../pasteurization-status-tile/pasteurization-status-tile.component';
import { MixitX509BannerComponent } from '../../mixit-x509-banner/mixit-x509-banner.component';
import { AsyncPipe } from '@angular/common';
import { AlarmBarComponent } from '../../alarms/alarm-bar/alarm-bar.component';
import { SchematicsTileComponent } from '../../schematics-tile/schematics-tile.component';
import { SetpointTileComponent } from '../../setpoint-tile/setpoint-tile.component';
import { PumpStatusTileComponent } from '../../pump-status-tile/pump-status-tile.component';
import { ValveStatusTileComponent } from '../../valve-status-tile/valve-status-tile.component';
import { HeatMeterStatusTileComponent } from '../../heat-meter-status-tile/heat-meter-status-tile.component';
import { ForwardReturnChartTileComponent } from '../../charts/forward-return-chart-tile/forward-return-chart-tile.component';
import { ValveChartComponent } from '../../charts/valve-chart/valve-chart.component';
import { EnergyValveHeatAndFlowChartComponent } from '../../charts/energy-valve-heat-and-flow-chart/energy-valve-heat-and-flow-chart.component';
import { EnergyValveTemperatureChartComponent } from '../../charts/energy-valve-temperature-chart/energy-valve-temperature-chart.component';
import { FlowHeatMeterChartTileComponent } from '../../charts/flow-heat-meter-chart-tile/flow-heat-meter-chart-tile.component';
import { WaterMeterChartTileComponent } from '../../charts/water-meter-chart-tile/water-meter-chart-tile.component';

@Component({
    selector: 'app-hot-water-dashboard',
    templateUrl: './hot-water-dashboard.component.html',
    styleUrls: ['./hot-water-dashboard.component.scss'],
    imports: [
    PasteurizationStatusTileComponent,
    MixitX509BannerComponent,
    AlarmBarComponent,
    SchematicsTileComponent,
    SetpointTileComponent,
    PumpStatusTileComponent,
    ValveStatusTileComponent,
    HeatMeterStatusTileComponent,
    ForwardReturnChartTileComponent,
    TemperatureChartTileComponent,
    ValveChartComponent,
    EnergyValveHeatAndFlowChartComponent,
    EnergyValveTemperatureChartComponent,
    FlowHeatMeterChartTileComponent,
    WaterMeterChartTileComponent,
    TranslateModule,
    AsyncPipe
]
})
export class HotWaterDashboardComponent implements OnInit {
  @Input() public alarm$: Observable<AlarmOverview>;
  @Input() public application: Application;
  @Input() public installation$: Observable<Installation>;
  @Input() public schematic$: Observable<AppModel>;
  @Input() public dataPoints$: Observable<DataPointsResult>;

  public pumpDevice$: Observable<SystemDevice | undefined>;
  public pumpType$: Observable<string>;
  public pumpOptions$: Observable<PumpOption[] | undefined>;
  public valveDevice$: Observable<SystemDevice | undefined>;
  public tankChartSeries$: Observable<Serie[]>;
  public EEquipment = EEquipment;
  public hasEnergyValve$: Observable<boolean | undefined>;
  public hasPrimaryTemperatureSensors$: Observable<boolean | undefined>;
  private system$: Observable<HotWaterTankSystemState>;

  constructor(private schematicsService: SchematicsService, private translateService: TranslateService) {}

  ngOnInit(): void {
    this.system$ = this.schematic$.pipe(
      map((schematic) => {
        return schematic.systems.find((s) => s.hasId === this.application.id) as HotWaterTankSystemState;
      })
    );

    this.pumpDevice$ = this.dataPoints$.pipe(systemsToPumpDevice(this.application.id));

    this.pumpType$ = getPumpType(this.schematic$, this.application.id);

    this.pumpOptions$ = getPumpOptions(this.pumpDevice$, this.schematicsService, this.application.id);

    this.valveDevice$ = this.dataPoints$.pipe(systemsToValveDevice(this.application.id));

    this.hasEnergyValve$ = this.system$.pipe(
      map((system) => {
        return systemHasEnergyValve(system?.systemInfo);
      })
    );

    this.hasPrimaryTemperatureSensors$ = this.system$.pipe(
      map((system) => {
        return (system?.systemInfo.T_PF_present || system?.systemInfo.T_PR_present || system?.systemInfo.HM_present);
      })
    );

    const tankTop$ = this.translateService.get('commercial-hot-water-dashboard.tank-top');
    const tankCenter$ = this.translateService.get('commercial-hot-water-dashboard.tank-center');
    const tankBottom$ = this.translateService.get('commercial-hot-water-dashboard.tank-bottom');

    this.tankChartSeries$ = combineLatest([this.system$, tankTop$, tankCenter$, tankBottom$]).pipe(
      map(([system, tankTop, tankCenter, tankBottom]) => {
        if (!system?.systemInfo) {
          return [];
        }

        const series: Serie[] = [];

        series.push({
          type: SerieType.DataPoint,
          title: tankTop,
          dumbledoreId: EEquipment.T_T,
        });

        if (system.systemInfo.T_MIDDLE_present) {
          series.push({
            type: SerieType.DataPoint,
            title: tankCenter,
            dumbledoreId: EEquipment.T_MIDDLE,
          });
        }

        if (system.systemInfo.T_BOTTOM_present) {
          series.push({
            type: SerieType.DataPoint,
            title: tankBottom,
            dumbledoreId: EEquipment.T_BOTTOM,
          });
        }

        return series;
      })
    );
  }
}
